"use client";

import { forwardRef, type ReactNode } from "react";
import InfoBadge, { type InfoBadgeProps } from "~/componentsDS/InfoBadge";
import ContentTabs, { type ContentTab } from "~/componentsDS/ContentTabs";
import type { DrawerOpenOptions } from "~/components/layout/Drawer";
import useTotalValue from "~/hooks/useTotalValue";

type TotalValueBadgeBaseProps = Omit<InfoBadgeProps, "drawer" | "label"> & {
    /** Badge label, e.g. "Total Value 450$/mo". */
    label: ReactNode;
    /** Drawer heading shown when the badge is opened. */
    drawerTitle?: ReactNode;
    /**
     * Extra drawer options (size, side, classNames, ...) merged into the
     * generated drawer configuration.
     */
    drawerOptions?: Omit<DrawerOpenOptions, "title" | "content">;
};

type TotalValueBadgeTabsProps = TotalValueBadgeBaseProps & {
    /** Tabs rendered inside the drawer. Mutually exclusive with `drawerContent`. */
    tabs: ContentTab[];
    defaultTab?: string;
    drawerContent?: never;
};

type TotalValueBadgeContentProps = TotalValueBadgeBaseProps & {
    /** Fully custom drawer body. Mutually exclusive with `tabs`. */
    drawerContent: ReactNode;
    tabs?: never;
    defaultTab?: never;
};

export type TotalValueBadgeProps =
    | TotalValueBadgeTabsProps
    | TotalValueBadgeContentProps;

/**
 * Reusable "Total Value" badge that opens a drawer with details.
 *
 * This is the single point that gates the Oestra-redesign Total Value widget
 * behind the PQP_OESTRA_REDESIGN flag: when the flag is off it renders nothing,
 * so no hidden/unfinished content leaks into the DOM. Reuse this component
 * anywhere the badge is needed instead of wiring the flag check by hand.
 */
const TotalValueBadge = forwardRef<HTMLButtonElement, TotalValueBadgeProps>(
    (
        {
            label,
            drawerTitle = "Total value",
            drawerOptions,
            tabs,
            defaultTab,
            drawerContent,
            variant = "secondary",
            className,
            eventData,
            ...infoBadgeProps
        },
        ref
    ) => {
        const isTotalValueEnabled = useTotalValue();

        if (!isTotalValueEnabled) return null;

        const content =
            drawerContent ??
            (tabs ? (
                <ContentTabs
                    eventData={eventData}
                    tabs={tabs}
                    defaultValue={defaultTab ?? tabs[0]?.value}
                />
            ) : null);

        if (!content) return null;

        return (
            <InfoBadge
                ref={ref}
                eventData={eventData}
                label={label}
                variant={variant}
                className={className}
                drawer={{
                    ...drawerOptions,
                    title: drawerTitle,
                    content,
                }}
                {...infoBadgeProps}
            />
        );
    }
);

TotalValueBadge.displayName = "TotalValueBadge";

export default TotalValueBadge;
